import { useState } from 'react';
import { SearchIcon } from 'lucide-react';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';

import { mutateListOnDelete } from '@/helpers/swr';

import {
  Table,
  TableBody,
  TableHeader,
  TableHead,
  TableRow
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import {
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription
} from '@/components/ui/empty';

import { useActivitiesV2, useReorderActivities } from '@/data/activity';

import ActivityRow from './ActivityRow';

const ActivityList = () => {
  const activities = useActivitiesV2();
  const [activitiesOrder, setActivitiesOrder] = useState(activities.data.query);
  const reorderActivities = useReorderActivities();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  if (activities.data.query.length === 0) {
    return (
      <Empty>
        <EmptyIcon>
          <SearchIcon />
        </EmptyIcon>
        <EmptyTitle>No Activities Found</EmptyTitle>
        <EmptyDescription>Try adding an activity</EmptyDescription>
      </Empty>
    );
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    const oldIndex = activitiesOrder.indexOf(active.id);
    const newIndex = activitiesOrder.indexOf(over.id);
    const newQuery = arrayMove(activitiesOrder, oldIndex, newIndex);
    setActivitiesOrder(newQuery);

    const payload = newQuery.map((activityId, index) => ({
      id: activityId,
      sortOrder: index
    }));

    await reorderActivities(payload);
  };

  const handleDelete = async (activityId) => {
    setActivitiesOrder(activitiesOrder.filter((id) => id !== activityId));
    await activities.mutate(
      (current) => mutateListOnDelete(current, activityId),
      false
    );
  };

  return (
    <>
      <Card>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={activitiesOrder}
            strategy={verticalListSortingStrategy}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead></TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Fee</TableHead>
                  <TableHead></TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {activitiesOrder.map((activityId) => (
                  <ActivityRow
                    key={activityId}
                    activityId={activityId}
                    onDelete={handleDelete}
                  />
                ))}
              </TableBody>
            </Table>
          </SortableContext>
        </DndContext>
      </Card>
      <div className="text-center p-4">
        <span className="text-sm text-muted-foreground">
          Total: {activities.data.query.length}
        </span>
      </div>
    </>
  );
};

export default ActivityList;
